import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { CollectiblesService } from '../../../services/collectiblesService';
import marketplaceService from '../../../services/marketplaceService';

const ListingTypeSelector = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1: choose type, 2: select item, 3: configure
  const [listingMode, setListingMode] = useState(null); // 'auction' | 'marketplace'
  const [collectibles, setCollectibles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCollectible, setSelectedCollectible] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Auction form
  const [auctionForm, setAuctionForm] = useState({
    title: '',
    description: '',
    starting_bid: '',
    reserve_price: '',
    buy_it_now_price: '',
    bid_increment: '10.00',
    auction_type: 'regular',
    category_id: '',
    start_date: '',
    end_date: '',
    shipping_cost: '0',
    condition: ''
  });

  // Marketplace form
  const [marketForm, setMarketForm] = useState({
    title: '',
    description: '',
    price: '',
    condition: '',
    shipping_cost: '0',
    listing_type: 'fixed_price',
    accepts_offers: false
  });

  useEffect(() => {
    if (isOpen) {
      loadCollectibles();
      loadCategories();
      setStep(1);
      setListingMode(null);
      setSelectedCollectible(null);
    }
  }, [isOpen]);

  const loadCollectibles = async () => {
    setLoading(true);
    const { data } = await CollectiblesService?.getUserCollectibles(user?.id);
    setCollectibles(data || []);
    setLoading(false);
  };

  const loadCategories = async () => {
    const { data } = await marketplaceService?.getAuctionCategories();
    setCategories(data || []);
  };

  const handleSelectCollectible = (item) => {
    setSelectedCollectible(item);
    const defaultCondition = item?.condition || '';
    const defaultDesc = item?.description || '';
    const defaultTitle = item?.name || '';

    if (listingMode === 'auction') {
      setAuctionForm(prev => ({
        ...prev,
        title: defaultTitle,
        description: defaultDesc,
        starting_bid: item?.current_value ? String(Math.round(item?.current_value * 0.5)) : '',
        condition: defaultCondition
      }));
    } else {
      setMarketForm(prev => ({
        ...prev,
        title: defaultTitle,
        description: defaultDesc,
        price: item?.current_value ? String(item?.current_value) : '',
        condition: defaultCondition
      }));
    }
    setStep(3);
  };

  const handleSubmitAuction = async (e) => {
    e?.preventDefault();
    setSubmitting(true);

    const auctionData = {
      ...auctionForm,
      collectible_id: selectedCollectible?.id,
      seller_id: user?.id,
      featured_image_url: selectedCollectible?.primary_image_url || null,
      starting_bid: parseFloat(auctionForm?.starting_bid),
      reserve_price: auctionForm?.reserve_price ? parseFloat(auctionForm?.reserve_price) : null,
      buy_it_now_price: auctionForm?.buy_it_now_price ? parseFloat(auctionForm?.buy_it_now_price) : null,
      bid_increment: parseFloat(auctionForm?.bid_increment || 10),
      shipping_cost: parseFloat(auctionForm?.shipping_cost || 0),
      start_date: new Date(auctionForm?.start_date)?.toISOString(),
      end_date: new Date(auctionForm?.end_date)?.toISOString(),
      status: new Date(auctionForm?.start_date) <= new Date() ? 'active' : 'scheduled'
    };

    const { error } = await marketplaceService?.createAuction(auctionData);
    if (!error) {
      onSuccess?.('auction');
    } else {
      alert('Failed to create auction: ' + error);
    }
    setSubmitting(false);
  };

  const handleSubmitMarketplace = async (e) => {
    e?.preventDefault();
    setSubmitting(true);

    const listingData = {
      ...marketForm,
      collectible_id: selectedCollectible?.id,
      seller_id: user?.id,
      featured_image_url: selectedCollectible?.primary_image_url || null,
      price: parseFloat(marketForm?.price),
      shipping_cost: parseFloat(marketForm?.shipping_cost || 0),
      status: 'active'
    };

    const { error } = await marketplaceService?.createListing(listingData);
    if (!error) {
      onSuccess?.('marketplace');
    } else {
      alert('Failed to create listing: ' + error);
    }
    setSubmitting(false);
  };

  const filteredCollectibles = collectibles?.filter(c =>
    !searchTerm ||
    c?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    c?.collectible_type?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="Tag" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">List Your Collectible</h2>
              <p className="text-sm text-muted-foreground">
                {step === 1 ? 'Choose how to sell' : step === 2 ? 'Select an item from your collection' : 'Configure your listing'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-1">
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-6 pt-4">
          <div className="flex items-center gap-2">
            {[1, 2, 3]?.map((s) => (
              <React.Fragment key={s}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                  step >= s ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}>
                  {step > s ? <Icon name="Check" size={14} /> : s}
                </div>
                {s < 3 && <div className={`flex-1 h-0.5 transition-colors ${step > s ? 'bg-primary' : 'bg-border'}`} />}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-muted-foreground">Choose Type</span>
            <span className="text-xs text-muted-foreground">Select Item</span>
            <span className="text-xs text-muted-foreground">Configure</span>
          </div>
        </div>

        <div className="p-6">
          {/* Step 1: Choose listing type */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-base font-semibold text-foreground mb-4">How would you like to sell?</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Auction Option */}
                <button
                  onClick={() => { setListingMode('auction'); setStep(2); }}
                  className="group p-6 border-2 border-border hover:border-primary rounded-2xl text-left transition-all hover:bg-primary/5"
                >
                  <div className="w-14 h-14 bg-purple-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-purple-500/20 transition-colors">
                    <Icon name="Gavel" size={28} className="text-purple-500" />
                  </div>
                  <h4 className="text-lg font-bold text-foreground mb-2">Auction</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Let buyers compete with bids. Set a starting price, reserve, and timer. Great for rare or high-demand items.
                  </p>
                  <div className="space-y-1.5">
                    {['Timed bidding with countdown', 'Reserve price protection', 'Buy It Now option', 'Bid history & transparency']?.map(f => (
                      <div key={f} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Icon name="Check" size={12} className="text-purple-500 flex-shrink-0" />
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex items-center gap-2 text-purple-500 text-sm font-medium">
                    <span>Start Auction</span>
                    <Icon name="ArrowRight" size={16} />
                  </div>
                </button>

                {/* Marketplace Option */}
                <button
                  onClick={() => { setListingMode('marketplace'); setStep(2); }}
                  className="group p-6 border-2 border-border hover:border-primary rounded-2xl text-left transition-all hover:bg-primary/5"
                >
                  <div className="w-14 h-14 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">
                    <Icon name="Store" size={28} className="text-blue-500" />
                  </div>
                  <h4 className="text-lg font-bold text-foreground mb-2">Marketplace</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Set a fixed price for instant purchase. Buyers can buy immediately or make offers.
                  </p>
                  <div className="space-y-1.5">
                    {['Fixed price listing', 'Accept or decline offers', 'Instant purchase', 'Escrow-protected payment']?.map(f => (
                      <div key={f} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Icon name="Check" size={12} className="text-blue-500 flex-shrink-0" />
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex items-center gap-2 text-blue-500 text-sm font-medium">
                    <span>Create Listing</span>
                    <Icon name="ArrowRight" size={16} />
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Select collectible */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-foreground">
                  Select item to {listingMode === 'auction' ? 'auction' : 'list'}
                </h3>
                <button
                  onClick={() => setStep(1)}
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                >
                  <Icon name="ArrowLeft" size={14} />
                  Back
                </button>
              </div>

              <div className="relative">
                <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search your collection..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full pl-9 pr-4 py-2.5 bg-background border border-input rounded-lg text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
                </div>
              ) : filteredCollectibles?.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="Package" size={40} className="mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No collectibles found in your collection</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-80 overflow-y-auto">
                  {filteredCollectibles?.map(item => (
                    <button
                      key={item?.id}
                      onClick={() => handleSelectCollectible(item)}
                      className="group p-3 border border-border hover:border-primary rounded-xl text-left transition-all hover:bg-primary/5"
                    >
                      <div className="aspect-square bg-muted rounded-lg overflow-hidden mb-2">
                        {item?.primary_image_url ? (
                          <img src={item?.primary_image_url} alt={item?.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Icon name="Image" size={24} className="text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <p className="text-xs font-semibold text-foreground truncate">{item?.name}</p>
                      <p className="text-xs text-muted-foreground capitalize">{item?.collectible_type?.replace('-', ' ')}</p>
                      {item?.current_value && (
                        <p className="text-xs text-primary font-medium mt-1">${parseFloat(item?.current_value)?.toFixed(0)}</p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Configure listing */}
          {step === 3 && selectedCollectible && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-foreground">
                  Configure {listingMode === 'auction' ? 'Auction' : 'Marketplace Listing'}
                </h3>
                <button
                  onClick={() => setStep(2)}
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                >
                  <Icon name="ArrowLeft" size={14} />
                  Back
                </button>
              </div>

              {/* Selected Item Preview */}
              <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-xl border border-border">
                <div className="w-14 h-14 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                  {selectedCollectible?.primary_image_url ? (
                    <img src={selectedCollectible?.primary_image_url} alt={selectedCollectible?.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name="Image" size={20} className="text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div>
                  <p className="font-semibold text-foreground text-sm">{selectedCollectible?.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{selectedCollectible?.collectible_type?.replace('-', ' ')}</p>
                  <div className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    listingMode === 'auction' ? 'bg-purple-500/10 text-purple-500' : 'bg-blue-500/10 text-blue-500'
                  }`}>
                    <Icon name={listingMode === 'auction' ? 'Gavel' : 'Store'} size={10} />
                    {listingMode === 'auction' ? 'Auction' : 'Marketplace'}
                  </div>
                </div>
              </div>

              {/* Auction Form */}
              {listingMode === 'auction' && (
                <form onSubmit={handleSubmitAuction} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setAuctionForm(p => ({ ...p, auction_type: 'regular' }))}
                      className={`p-3 border-2 rounded-xl text-left transition-all ${
                        auctionForm?.auction_type === 'regular' ? 'border-primary bg-primary/5' : 'border-border'
                      }`}
                    >
                      <Icon name="Clock" size={18} className={auctionForm?.auction_type === 'regular' ? 'text-primary mb-1' : 'text-muted-foreground mb-1'} />
                      <p className="text-sm font-semibold text-foreground">Regular</p>
                      <p className="text-xs text-muted-foreground">Standard timed auction</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAuctionForm(p => ({ ...p, auction_type: 'premiere' }))}
                      className={`p-3 border-2 rounded-xl text-left transition-all ${
                        auctionForm?.auction_type === 'premiere' ? 'border-primary bg-primary/5' : 'border-border'
                      }`}
                    >
                      <Icon name="Star" size={18} className={auctionForm?.auction_type === 'premiere' ? 'text-primary mb-1' : 'text-muted-foreground mb-1'} />
                      <p className="text-sm font-semibold text-foreground">Premiere</p>
                      <p className="text-xs text-muted-foreground">Monthly featured event</p>
                    </button>
                  </div>

                  <Input
                    label="Auction Title"
                    required
                    value={auctionForm?.title}
                    onChange={(e) => setAuctionForm(p => ({ ...p, title: e?.target?.value }))}
                    placeholder="Enter auction title"
                  />

                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">Description <span className="text-destructive">*</span></label>
                    <textarea
                      required
                      value={auctionForm?.description}
                      onChange={(e) => setAuctionForm(p => ({ ...p, description: e?.target?.value }))}
                      placeholder="Describe your item..."
                      rows={3}
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Starting Bid ($)"
                      type="number"
                      step="0.01"
                      required
                      value={auctionForm?.starting_bid}
                      onChange={(e) => setAuctionForm(p => ({ ...p, starting_bid: e?.target?.value }))}
                      placeholder="0.00"
                    />
                    <Input
                      label="Reserve Price ($)"
                      type="number"
                      step="0.01"
                      value={auctionForm?.reserve_price}
                      onChange={(e) => setAuctionForm(p => ({ ...p, reserve_price: e?.target?.value }))}
                      placeholder="Optional"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Buy It Now Price ($)"
                      type="number"
                      step="0.01"
                      value={auctionForm?.buy_it_now_price}
                      onChange={(e) => setAuctionForm(p => ({ ...p, buy_it_now_price: e?.target?.value }))}
                      placeholder="Optional"
                    />
                    <Input
                      label="Bid Increment ($)"
                      type="number"
                      step="0.01"
                      required
                      value={auctionForm?.bid_increment}
                      onChange={(e) => setAuctionForm(p => ({ ...p, bid_increment: e?.target?.value }))}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Start Date & Time"
                      type="datetime-local"
                      required
                      value={auctionForm?.start_date}
                      onChange={(e) => setAuctionForm(p => ({ ...p, start_date: e?.target?.value }))}
                    />
                    <Input
                      label="End Date & Time"
                      type="datetime-local"
                      required
                      value={auctionForm?.end_date}
                      onChange={(e) => setAuctionForm(p => ({ ...p, end_date: e?.target?.value }))}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-foreground mb-1.5 block">Condition <span className="text-destructive">*</span></label>
                      <Select
                        value={auctionForm?.condition}
                        onChange={(e) => setAuctionForm(p => ({ ...p, condition: e?.target?.value }))}
                        required
                      >
                        <option value="">Select condition</option>
                        <option value="mint">Mint</option>
                        <option value="near-mint">Near Mint</option>
                        <option value="excellent">Excellent</option>
                        <option value="very-good">Very Good</option>
                        <option value="good">Good</option>
                        <option value="fair">Fair</option>
                        <option value="poor">Poor</option>
                      </Select>
                    </div>
                    <Input
                      label="Shipping Cost ($)"
                      type="number"
                      step="0.01"
                      value={auctionForm?.shipping_cost}
                      onChange={(e) => setAuctionForm(p => ({ ...p, shipping_cost: e?.target?.value }))}
                    />
                  </div>

                  {auctionForm?.auction_type === 'premiere' && (
                    <div>
                      <label className="text-sm font-medium text-foreground mb-1.5 block">Premiere Category <span className="text-destructive">*</span></label>
                      <Select
                        value={auctionForm?.category_id}
                        onChange={(e) => setAuctionForm(p => ({ ...p, category_id: e?.target?.value }))}
                        required
                      >
                        <option value="">Select category...</option>
                        {categories?.map(cat => (
                          <option key={cat?.id} value={cat?.id}>{cat?.name}</option>
                        ))}
                      </Select>
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={onClose} fullWidth>Cancel</Button>
                    <Button type="submit" loading={submitting} iconName="Gavel" iconPosition="left" fullWidth>
                      Create Auction
                    </Button>
                  </div>
                </form>
              )}

              {/* Marketplace Form */}
              {listingMode === 'marketplace' && (
                <form onSubmit={handleSubmitMarketplace} className="space-y-4">
                  <Input
                    label="Listing Title"
                    required
                    value={marketForm?.title}
                    onChange={(e) => setMarketForm(p => ({ ...p, title: e?.target?.value }))}
                    placeholder="Enter listing title"
                  />

                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">Description <span className="text-destructive">*</span></label>
                    <textarea
                      required
                      value={marketForm?.description}
                      onChange={(e) => setMarketForm(p => ({ ...p, description: e?.target?.value }))}
                      placeholder="Describe your item..."
                      rows={3}
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Price ($)"
                      type="number"
                      step="0.01"
                      required
                      value={marketForm?.price}
                      onChange={(e) => setMarketForm(p => ({ ...p, price: e?.target?.value }))}
                      placeholder="0.00"
                    />
                    <Input
                      label="Shipping Cost ($)"
                      type="number"
                      step="0.01"
                      value={marketForm?.shipping_cost}
                      onChange={(e) => setMarketForm(p => ({ ...p, shipping_cost: e?.target?.value }))}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">Condition <span className="text-destructive">*</span></label>
                    <Select
                      value={marketForm?.condition}
                      onChange={(e) => setMarketForm(p => ({ ...p, condition: e?.target?.value }))}
                      required
                    >
                      <option value="">Select condition</option>
                      <option value="mint">Mint</option>
                      <option value="near-mint">Near Mint</option>
                      <option value="excellent">Excellent</option>
                      <option value="very-good">Very Good</option>
                      <option value="good">Good</option>
                      <option value="fair">Fair</option>
                      <option value="poor">Poor</option>
                    </Select>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-xl border border-border">
                    <input
                      type="checkbox"
                      id="accepts_offers"
                      checked={marketForm?.accepts_offers}
                      onChange={(e) => setMarketForm(p => ({ ...p, accepts_offers: e?.target?.checked }))}
                      className="w-4 h-4 rounded border-input"
                    />
                    <label htmlFor="accepts_offers" className="text-sm text-foreground cursor-pointer">
                      Accept offers from buyers
                    </label>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={onClose} fullWidth>Cancel</Button>
                    <Button type="submit" loading={submitting} iconName="Store" iconPosition="left" fullWidth>
                      Create Listing
                    </Button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListingTypeSelector;
